/**
 * Sentry error tracking for the SPA. Off unless `VITE_SENTRY_DSN` is set when
 * the bundle is built: Vite inlines it (see `frontend/Dockerfile`).
 *
 * Errors only, and nothing about the people using the app or the data they see.
 * It mirrors the backend's `error_tracking.py`:
 *
 * - `dataCollection` turns off what the SDK would otherwise collect (v11
 *   replaced `sendDefaultPii` with it, and its defaults collect everything): no
 *   IP address or user info, cookies, bodies or query strings, and only the
 *   User-Agent header. No tracing and no Session Replay are loaded.
 * - `scrubEvent` (beforeSend) keeps a request's URL without its query string,
 *   applies `scrubBreadcrumb` to the breadcrumbs and masks sensitive keys.
 * - `scrubBreadcrumb` (beforeBreadcrumb) drops console output and fetch/XHR
 *   breadcrumbs that carry a body, and strips the query string of other URLs,
 *   those inside a breadcrumb's message included.
 */
import { type Breadcrumb, type ErrorEvent, init, type RequestEventData } from "@sentry/react";

import { logger } from "@/lib/logger";

/** What a masked value is replaced with, as Sentry's own scrubbers write it. */
export const FILTERED = "[Filtered]";

/** Key fragments whose values are masked (case-insensitive, `-` read as `_`). */
const SENSITIVE_KEY_FRAGMENTS = [
  "api_key",
  "apikey",
  "authorization",
  "cookie",
  "credential",
  "passwd",
  "password",
  "private_key",
  "secret",
  "session",
  "token",
];
/** Key prefixes masked the same way: Odoo headers and MCP tool data, as in the backend. */
const SENSITIVE_KEY_PREFIXES = [
  "x_odoo",
  "mcp.request.argument.",
  "mcp.tool.result.content",
  "mcp.prompt.result.message_content",
];

/** The only request headers an event keeps. */
const KEPT_HEADERS = new Set(["user-agent"]);

const HTTP_CATEGORIES = new Set(["fetch", "xhr"]);
/** Breadcrumb data keys that hold a body: a fetch/XHR breadcrumb with one is dropped. */
const BODY_KEYS = ["body", "data", "request_body", "response_body", "requestBody", "responseBody"];
/** Breadcrumb data keys that hold a query string or fragment: removed. */
const QUERY_KEYS = ["http.query", "http.fragment"];
/** Breadcrumb data keys that hold a URL (fetch/XHR `url`, navigation `from`/`to`). */
const URL_KEYS = ["url", "from", "to"];
/**
 * A URL inside free text such as a breadcrumb's message, whose query string and
 * fragment are stripped: an absolute URL (`https://host/path?q=…`), or a path
 * that starts a word or follows a quote, a bracket or `=` (`/api/me?q=…`). It
 * runs to the next space, quote or angle bracket, so trailing punctuation goes
 * with the query: over-stripping is safe. Mirrors the backend's `_URL_IN_TEXT`.
 */
const URL_IN_TEXT = /(?:\b[a-z][a-z0-9+.-]*:\/\/|(?<![^\s"'([<=])\/)[^\s"'<>]+/gi;
/** Where the SDK keeps its own live objects on an event; never sent. */
const SDK_METADATA_KEY = "sdkProcessingMetadata";

/**
 * Start Sentry when `VITE_SENTRY_DSN` was set at build time; otherwise do
 * nothing. Call it once, before the app renders.
 */
export function initSentry(): boolean {
  const dsn = (import.meta.env.VITE_SENTRY_DSN ?? "").trim();
  if (!dsn) {
    logger.debug("[sentry]", "off: no VITE_SENTRY_DSN");
    return false;
  }
  init({
    dsn,
    environment: import.meta.env.MODE,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: { allow: ["user-agent"] }, response: false },
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
    },
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  logger.debug("[sentry]", "on", import.meta.env.MODE);
  return true;
}

/** Strip an outgoing error event down to what is safe to send to Sentry. */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  // Fails closed: anything of an unexpected shape is dropped, not sent as it is.
  if ("request" in event) {
    event.request = scrubRequest(event.request);
  }
  if ("breadcrumbs" in event) {
    const breadcrumbs: unknown = event.breadcrumbs;
    if (Array.isArray(breadcrumbs)) {
      event.breadcrumbs = breadcrumbs
        .filter(
          (crumb: unknown): crumb is Breadcrumb =>
            typeof crumb === "object" && crumb !== null && isPlainObject(crumb),
        )
        .map(scrubBreadcrumb)
        .filter((crumb): crumb is Breadcrumb => crumb !== null);
    } else {
      delete event.breadcrumbs;
    }
  }
  maskSensitiveKeys(event);
  return event;
}

/** Scrub a breadcrumb as it is recorded; `null` drops it. */
export function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb | null {
  // Console output can hold API responses, emails and anything else logged.
  if (crumb.category === "console") {
    return null;
  }
  // `data` should be an object; anything else (a string, a number, an array) cannot be
  // scrubbed key by key, so it is dropped rather than sent as it is.
  const raw: unknown = crumb.data;
  const source = typeof raw === "object" && raw !== null && isPlainObject(raw) ? raw : undefined;
  const isHttp = crumb.type === "http" || HTTP_CATEGORIES.has(crumb.category ?? "");
  if (source && isHttp && BODY_KEYS.some((key) => key in source)) {
    return null;
  }
  const scrubbed: Breadcrumb = { ...crumb };
  if (typeof crumb.message === "string") {
    scrubbed.message = stripQueriesInText(crumb.message);
  }
  if (!source) {
    delete scrubbed.data;
    return scrubbed;
  }
  const data = { ...source };
  for (const key of QUERY_KEYS) {
    delete data[key];
  }
  for (const key of URL_KEYS) {
    if (typeof data[key] === "string") {
      data[key] = stripQuery(data[key]);
    }
  }
  return { ...scrubbed, data };
}

/** Keep a request's URL without query string, its method and the kept headers. */
function scrubRequest(raw: unknown): RequestEventData {
  const scrubbed: RequestEventData = {};
  if (typeof raw !== "object" || raw === null || !isPlainObject(raw)) {
    return scrubbed;
  }
  if (typeof raw.url === "string") {
    scrubbed.url = stripQuery(raw.url);
  }
  if (typeof raw.method === "string") {
    scrubbed.method = raw.method;
  }
  const headers = raw.headers;
  if (typeof headers === "object" && headers !== null && isPlainObject(headers)) {
    scrubbed.headers = Object.fromEntries(
      Object.entries(headers).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === "string" && KEPT_HEADERS.has(entry[0].toLowerCase()),
      ),
    );
  }
  return scrubbed;
}

/**
 * Replace the value of every sensitive key, at any depth, with FILTERED.
 *
 * Walks plain objects and arrays only, and skips `sdkProcessingMetadata`: the
 * SDK keeps live objects such as its scopes there, which are never sent and
 * must not be changed.
 */
function maskSensitiveKeys(value: unknown, seen = new WeakSet<object>()): void {
  if (value === null || typeof value !== "object" || seen.has(value)) {
    return;
  }
  seen.add(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      maskSensitiveKeys(item, seen);
    }
    return;
  }
  if (!isPlainObject(value)) {
    return;
  }
  for (const key of Object.keys(value)) {
    if (key === SDK_METADATA_KEY) {
      continue;
    }
    if (isSensitiveKey(key)) {
      value[key] = FILTERED;
    } else {
      maskSensitiveKeys(value[key], seen);
    }
  }
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isSensitiveKey(key: string): boolean {
  const normalised = key.toLowerCase().replaceAll("-", "_");
  return (
    SENSITIVE_KEY_PREFIXES.some((prefix) => normalised.startsWith(prefix)) ||
    SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalised.includes(fragment))
  );
}

/** Free text with the query string and fragment of every URL in it removed. */
function stripQueriesInText(text: string): string {
  return text.replace(URL_IN_TEXT, (url) => stripQuery(url));
}

/** A URL without its query string and fragment. */
function stripQuery(url: string): string {
  return url.replace(/[?#].*$/s, "");
}
